import { Constants } from "appConstants";
import { IcoHint } from "assets/icons";
import ObjectHelper from "helpers/ObjectHelper";
import { OptionLike } from "helpers/OptionHelper";
import { useCallback, useMemo } from "react";
import Select, { createFilter, OptionProps as ReactSelectOptionProps } from "react-select";
import { Tooltip } from "react-tooltip";
import { ArrowDropdownIndicator, stylesSelect, themeSelect } from ".";
import "./styles.scss";
import { MultiSelectProps } from "./types";

const selectAllValue = "selectAll";

export const optionWithCheckbox = (props: ReactSelectOptionProps<OptionLike>): JSX.Element => {
	const { data, isSelected, innerProps, label, selectProps } = props;
	const { value } = data;
	const selectedValues = selectProps.value as OptionLike[];
	const selectedOrSelectAll =
		isSelected || (value === selectAllValue && selectProps.isMulti && (selectedValues?.length || 0) + 1 === selectProps.options?.length); // +1 because of select all option

	return (
		<div className="flex items-center custom-multiselect-option-with-checkbox" {...innerProps}>
			<div>
				<div className="select__option">{label}</div>
			</div>
			<input type="checkbox" checked={selectedOrSelectAll} readOnly className="select-option-checkbox" />
		</div>
	);
};

export default function MultiSelect<O extends OptionLike, V = string[] | O[]>(props: Readonly<MultiSelectProps<O, V>>): JSX.Element {
	const {
		label,
		className,
		name,
		tooltip,
		error,
		touched,
		short = false,
		noOptionsMessage = "Nenhuma opção",
		placeholder = "Selecione",
		setFieldValue,
		onChange,
		isDisabled,
		value,
		options,
		searchFields,
		getOptionValue,
		asSimpleValues = false,
		useOuterScroll = false,
		tooltipId,
		allowSelectAll = false,
		showCheckbox = false,
		shouldShowSelectedValues = true,
		isRequired = true
	} = props;

	const selectAllOption = useMemo(() => ({ label: "Selecionar todos", value: selectAllValue }), []);

	const selectedValues = useMemo<O[]>(() => {
		if (!asSimpleValues) return value as O[];
		return (value as string[]).map((v) => options?.find((opt) => opt.value === v)).filter((i) => i !== undefined) as O[];
	}, [value, options, asSimpleValues]);

	const optionsWithSelectAll = useMemo(() => {
		if (!allowSelectAll) return options;
		if (!showCheckbox && options?.length === selectedValues?.length) return options;
		return [selectAllOption, ...(options || [])];
	}, [options, allowSelectAll, selectedValues]);

	const onSelectAll = (newValue: readonly O[]): O[] => {
		if (newValue?.length === optionsWithSelectAll?.length) return [];
		return options || [];
	};

	const onSelectChange = useCallback(
		(newValue: readonly O[]): void => {
			let valueToChange = newValue;
			if (selectAllValue === newValue.find((v) => v.value === selectAllValue)?.value) {
				valueToChange = onSelectAll(valueToChange);
			}
			onChange?.(valueToChange);
			const newFieldValues: V = (asSimpleValues ? valueToChange.map(getOptionValue) : [...valueToChange]) as V;
			setFieldValue(name, newFieldValues);
		},
		[name, asSimpleValues, options]
	);

	const filterConfig: any = {
		ignoreCase: true,
		ignoreAccents: true,
		trim: true,
		matchFrom: "any",
		stringify: (option: ReactSelectOptionProps) => {
			const { data } = option || {};
			if (!searchFields) return option.label;

			const dataValues = searchFields.map((fieldName: string) => ObjectHelper.getPropertyFromObject(data, fieldName)).join(" ");
			return dataValues;
		}
	};

	const innerTooltipId = useMemo(() => tooltipId ?? `tooltip-${Math.trunc(Math.random() * 100000)}`, []);

	const placeholderOrSummaryValues = useMemo(
		() =>
			shouldShowSelectedValues
				? placeholder
				: selectedValues?.length > 0
				? `${selectedValues?.length} ${selectedValues?.length === 1 ? "item selecionado" : "itens selecionados"}`
				: placeholder,
		[placeholder, selectedValues]
	);

	return (
		<div className={`select-form-container ${className || ""}`}>
			{label && (
				<div className="flex gap-1 items-center pb-1">
					<label className="text-sm" htmlFor={name}>
						{label}
					</label>
					{isRequired && <span className="text-feedback-negative-pure-500"> *</span>}
					{tooltip && (
						<>
							<span id={innerTooltipId}>
								<IcoHint size="18" />
							</span>
							<Tooltip anchorSelect={`#${innerTooltipId}`} style={{ zIndex: Constants.zIndexes.tooltip }}>
								<div className="max-w-[96vw] md:max-w-[30rem] text-wrap">{tooltip}</div>
							</Tooltip>
						</>
					)}
				</div>
			)}
			<Select
				{...props}
				className={`custom-multiselect ${error && touched ? "error" : ""} ${short ? "short" : ""}`}
				classNamePrefix="select"
				menuPlacement="auto"
				menuPosition="fixed"
				minMenuHeight={200}
				isDisabled={isDisabled || !setFieldValue}
				options={optionsWithSelectAll}
				value={selectedValues}
				onChange={(n) => {
					onSelectChange(n as readonly O[]);
				}}
				components={{
					DropdownIndicator: ArrowDropdownIndicator as any,
					IndicatorSeparator: () => null,
					...(showCheckbox ? { Option: optionWithCheckbox } : {})
				}}
				styles={stylesSelect<OptionLike, true>(short, useOuterScroll)}
				theme={themeSelect()}
				noOptionsMessage={() => noOptionsMessage}
				placeholder={placeholderOrSummaryValues}
				filterOption={createFilter(filterConfig)}
				closeMenuOnSelect={false}
				isMulti
				hideSelectedOptions={!showCheckbox}
				controlShouldRenderValue={shouldShowSelectedValues}
			/>
			{error && touched && <div className="white-space--pre-line mt-1 text-xs">{Array.isArray(error) ? error.join("\n") : error}</div>}
		</div>
	);
}
